import React, { useState, useContext, useEffect } from "react";
import { AppContext } from "../Contexts/AppContextProvider";
import { HTTPResponse } from "../Helpers/Enums";
import { UserLoginService } from "../WebApiServices/UserLogin.service";
import { UserLoginMessage } from "../Helpers/HelperText";
import { useNavigate } from "react-router-dom";
import { AddLocalStorageData, IsEmailValid } from "../Helpers/Utilities";
import { Config } from "../Helpers/Config";
import { RecaptchaService } from "../WebApiServices/Recaptch.service";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";

export const Login = () => {
  const { ToggleLoader } = useContext(AppContext);
  const navigate = useNavigate();
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [credsError, setCredsError] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  // const [token, setToken] = useState("");
  // const [isTokenVerified, setIsTokenVerified] = useState(false);

  // Get the executeRecaptcha function from the hook
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Validate credentials
  const Validate = () => {
    let isValid = true;
    if (!creds.email) {
      setEmailError(UserLoginMessage.LoginEmailBlank);
      isValid = false;
    } else if (!IsEmailValid(creds.email)) {
      setEmailError(UserLoginMessage.LoginInvalidEmail);
      isValid = false;
    } else {
      setEmailError("");
    }

    if (!creds.password) {
      setPasswordError(UserLoginMessage.LoginPasswordBlank);
      isValid = false;
    } else {
      setPasswordError("");
    }
    return isValid;
  };

  // Handler for the login button click
  const OnLoginButtonClicked = async (e) => {
    e.preventDefault();

    if (!Validate()) {
      return;
    }

    if (!executeRecaptcha) {
      setCaptchaError(UserLoginMessage.reCAPTCHAFail);
      return;
    }

    try {
      ToggleLoader(true);
      // Generate token on demand
      const newToken = await executeRecaptcha("login");
      if (!newToken) {
        setCaptchaError(UserLoginMessage.reCAPTCHAFail);
        ToggleLoader(false);
        return;
      }
      // setToken(newToken);
      // setIsTokenVerified(true);
      setCaptchaError("");

      // Verify token with backend
      const recaptchaResponse = await RecaptchaService.Verify({
        token: newToken,
      });
      if (!recaptchaResponse.data.success) {
        setCaptchaError(UserLoginMessage.reCAPTCHAFail);
        // setToken("");
        // setIsTokenVerified(false);
        ToggleLoader(false);
        return;
      }

      // Proceed with login API call
      const response = await UserLoginService.UserLogin({
        email: creds.email,
        password: creds.password,
      });
      if (response.status !== HTTPResponse.OK) {
        setCredsError(UserLoginMessage.LogginFailure);
        // setToken("");
        // setIsTokenVerified(false);
      } else {
        if (response?.data?.isActive !== true) {
          setCredsError(UserLoginMessage.InactiveAbstractor);
          // setToken("");
          // setIsTokenVerified(false);
          ToggleLoader(false);
          return;
        } else if (response?.data?.locked === true) {
          setCredsError(UserLoginMessage.LoginLocked);
          // setToken("");
          // setIsTokenVerified(false);
          ToggleLoader(false);
        } else {
          AddLocalStorageData("userId", response?.data?.abstractorId ?? 0);
          const userArr = {
            userId: response?.data?.abstractorId ?? 0,
            userRole: response?.data?.roleId ?? 0,
            empName: response?.data?.employeeName ?? "",
            centerId: response?.data?.centerId ?? 0,
            centerName: response?.data?.centerName ?? "",
          };
          AddLocalStorageData("user", JSON.stringify(userArr));
          navigate("/ManagePatients");
        }
      }
    } catch (error) {
      // console.error("Error during login:", error);
      setCredsError(UserLoginMessage.LogginFailure);
      // setToken("");
      // setIsTokenVerified(false);
    } finally {
      ToggleLoader(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = async (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        await OnLoginButtonClicked(event);
      }
    };
    // Add event listener for the 'Enter' key when the component mounts
    document.addEventListener("keydown", handleKeyDown);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [creds]);

  const OnEmailChange = (e) => {
    setCreds((prev) => ({ ...prev, email: e.target.value }));
    setEmailError("");
    setCredsError("");
  };

  const OnPasswordChange = (e) => {
    setCreds((prev) => ({ ...prev, password: e.target.value }));
    setPasswordError("");
    setCredsError("");
  };

  const OnForgotPasswordClicked = () => {
    navigate("/PasswordRecovery");
  };

  return (
    <div className="gradient-custom admin-login">
      <div className="container py-5 custom-container">
        <div className="row justify-content-center align-items-center h-100">
          <div className="col-12 col-md-8 col-lg-6 col-xl-5">
            <div
              className="card bg-white text-black"
              style={{ borderRadius: "1rem" }}
            >
              <div className="card-body p-4">
                <div className="mb-md-2 mt-md-2 pb-2">
                  <h2 className="fw-bold mb-4 text-uppercase text-center login-heading">
                    Login
                  </h2>
                  <div className="form-outline form-white mb-4">
                    <input
                      type="email"
                      id="typeEmailX"
                      placeholder="Email"
                      className="form-control form-control-lg placeholder-font-size"
                      value={creds.email}
                      onChange={OnEmailChange}
                    />
                    {emailError && (
                      <div className="invalid-message">
                        <i
                          className="fa fa-exclamation-circle"
                          aria-hidden="true"
                        ></i>
                        <div className="invalid-login-input">{emailError}</div>
                      </div>
                    )}
                  </div>
                  <div className="form-outline form-white mb-1">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      id="password-id"
                      placeholder="Password"
                      value={creds.password}
                      onChange={OnPasswordChange}
                    />
                    <div
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <i className="fa fa-eye" id="togglePassword"></i>
                      ) : (
                        <i className="fa fa-eye fa-eye-slash"></i>
                      )}
                    </div>
                    {passwordError && (
                      <div className="invalid-message">
                        <i
                          className="fa fa-exclamation-circle"
                          aria-hidden="true"
                        ></i>
                        <div className="invalid-login-input">
                          {passwordError}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="forgot-password-hyperlink pointer-class">
                    <span onClick={OnForgotPasswordClicked}>
                      Forgot Password
                    </span>
                  </div>

                  {/* Display reCAPTCHA error if any */}
                  {captchaError && (
                    <div
                      className="invalid-login-input"
                      style={{ marginTop: 0, textAlign: "center" }}
                    >
                      {captchaError}
                    </div>
                  )}

                  <div className="text-center mt-2 mb-2">
                    <center>
                      <button
                        className="btn btn-outline-light btn-lg px-5 login-button"
                        onClick={OnLoginButtonClicked}
                      >
                        Login
                      </button>
                    </center>
                  </div>
                  <br />
                  {credsError && (
                    <div className="invalid-message">
                      <div className="invalid-login-input">
                        <center>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: credsError,
                            }}
                          ></span>

                          {/* {credsError} */}
                        </center>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrap Login component with the GoogleReCaptchaProvider
const LoginWithProvider = () => (
  <GoogleReCaptchaProvider reCaptchaKey={Config.reCAPTCHASiteKey}>
    <Login />
  </GoogleReCaptchaProvider>
);

export default LoginWithProvider;
